import './ProgramPage.scss'

import React, { useEffect } from 'react'
import { RouteComponentProps } from 'react-router'
import { useTranslation } from 'react-i18next'
import { find, head, isNil } from 'lodash'

import AdArea from '../../components/AdArea'
import SideContents from '../../components/SideContents'
import MobilePopularContentsCarousel from '../../components/MobileSideContentsCarousel'
import ProgramDetail from './ProgramDetail'
import ErrorBoundary from '../../components/ErrorBoundary'
import ProgramPageMeta from './ProgramPageMeta'
import EpisodeListContainer from '../episode/EpisodeListContainer'

import usePrevious from '../../hooks/usePrevious'
import useRefreshAd from '../../hooks/useRefreshAd'
import { getRedirectUrlByErrorcode } from '../../utils/misc'
import { adSpots, setAdvertisementState, initializeAdState } from '../../advertisement'
import { AdAreaHeight, HTTPStatus, ReleaseStatus } from '../../constants/enums'

import { useProgramQuery } from '../../types/generated'

interface MatchParams {
  programSlug: string
}

interface Props extends RouteComponentProps<MatchParams> {}

export default function ProgramPage({ match, history }: Props) {
  const { programSlug } = match.params
  const { data, refetch, error } = useProgramQuery({
    variables: { programSlug },
  })
  const { t } = useTranslation()
  const prevProgramSlug = usePrevious(programSlug)

  const program = data?.program ?? null
  const category = isNil(program) ? '' : head(program.categories)?.slug
  const articleMidAdSpot = find(adSpots, { name: 'ODC_ARTICLE_MID' })!
  const articleBottomAdSpot = find(adSpots, { name: 'ODC_ARTICLE_BOTTOM' })!

  useRefreshAd([program, category ?? ''])

  useEffect(() => {
    if (prevProgramSlug !== programSlug) {
      const { programSlug } = match.params
      refetch({ programSlug })
    }
    // eslint-disable-next-line
  }, [programSlug, prevProgramSlug])

  useEffect(() => {
    if (!error) {
      return
    }
    const redirectUrl = getRedirectUrlByErrorcode(error.graphQLErrors[0].extensions?.status ?? HTTPStatus.NOT_FOUND)
    if (redirectUrl) {
      history.push(redirectUrl)
    }
    // eslint-disable-next-line
  }, [error, programSlug])

  useEffect(() => {
    setAdvertisementState({ program: programSlug })
    if (category) {
      setAdvertisementState({ category })
    }
    setAdvertisementState({ partner: program?.contentProvider?.code ?? '' })
  }, [program, category, programSlug])

  useEffect(() => {
    return () => {
      initializeAdState()
    }
  }, [])

  return (
    <div className="ProgramPage">
      <h1 className="screen-reader-text">{t('program.h1')}</h1>
      {program && (
        <>
          <ProgramPageMeta program={program} />
          <div className="ProgramPage__contentWrapper">
            <div className="ProgramPage__content">
              <ErrorBoundary>
                <ProgramDetail program={program} />
              </ErrorBoundary>
              <AdArea
                className="ProgramPage__middleAd"
                adSpotId={articleMidAdSpot.spotId}
                height={AdAreaHeight.ARTICLE_MID}
              />
              <ErrorBoundary>
                <EpisodeListContainer
                  programSlug={program.slug}
                  episodeOrdering={program.meta?.episodeOrdering}
                  releaseStatus={program.releaseStatus as ReleaseStatus}
                  isSeries={program.meta?.isSeries ?? false}
                />
              </ErrorBoundary>
              <ErrorBoundary>
                <MobilePopularContentsCarousel />
              </ErrorBoundary>
              <AdArea
                className="ProgramPage__bottomAd"
                adSpotId={articleBottomAdSpot.spotId}
                height={AdAreaHeight.ARTICLE_BOTTOM}
              />
            </div>
            <ErrorBoundary>
              <SideContents />
            </ErrorBoundary>
          </div>
        </>
      )}
    </div>
  )
}
